import React, { useEffect, useRef } from 'react';
import { useWindowSize } from 'usehooks-ts';
import { PrimaryButton, publicSiteUrl, downloadToPUrl } from 'src/common';
import { HeadingOne, Modal } from 'src/features/common';
import QRCode from 'qrcode';
import { GooglePlayStore, VodaPayTapOnPhone, TapOnPhoneQRcode } from 'src/assets/images';
import { TopInstallInstructions } from 'src/components/tap-on-phone-modal/components/top-install-instructions';
import { useApplication } from 'src/hooks/useApplication';

interface TapOnPhoneModalProps {
  onClose: () => void;
}

const QR_CODE_SIZE = 120;

export const TapOnPhoneModal: React.FC<TapOnPhoneModalProps> = ({ onClose }) => {
  const { orderTealiumService } = useApplication();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const { width } = useWindowSize();

  useEffect(() => {
    orderTealiumService?.onShowTOPModal();
    setTimeout(() => {
      if (modalContentRef.current) {
        modalContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }
    }, 100);
  }, []);

  useEffect(() => {
    if (canvasRef.current) {
      QRCode.toCanvas(canvasRef.current, publicSiteUrl, {
        width: QR_CODE_SIZE,
      });
    }
  }, [canvasRef.current, width]);

  const goToAppStore = () => {
    window.location.href = `${downloadToPUrl}`;
  };

  const handleClose = () => {
    orderTealiumService?.onCloseTOPModal();
    onClose();
  };

  const onClickGetItHere = () => {
    orderTealiumService?.onGetItHereTOP();
  };

  return (
    <Modal size={'large'} className="!min-h-0 !bg-white-50" onClose={handleClose}>
      <section className="flex-col items-center hidden w-full gap-4 overflow-y-scroll md:flex">
        <div className="flex">
          <img src={VodaPayTapOnPhone} alt="vodapay logo" />
        </div>
        <HeadingOne>Lets get you started</HeadingOne>
        <p className="text-center text-lg font-normal w-[min(420px,_100%)]">
          You need smartphone to continue the journey. This must be the phone that you will use for
          Tap on Phone
        </p>
        <div className="w-6 h-1 m-0 mb-4 bg-red-600" />

        <div ref={modalContentRef} className="flex gap-4 p-2">
          <TopInstallInstructions heading="Option 1: Scan the QR Code">
            <p className="text-base">Scan the QR code below to get started.</p>
            <div className="flex justify-center">
              <img className="w-36" src={TapOnPhoneQRcode} alt="tap on phone QR code" />
            </div>
          </TopInstallInstructions>
          <TopInstallInstructions heading="Option 2: Download">
            <div className="flex flex-col flex-1">
              <p className="text-base font-medium">Using your mobile phone:</p>
              <ol className="pl-5 mt-2 text-base font-normal list-decimal">
                <li>Open the Google PlayStore</li>
                <li>Search for VodaPay Tap on Phone</li>
                <li>Install it</li>
              </ol>
            </div>
            <div className="flex justify-center">
              <img
                className="cursor-pointer w-36"
                onClick={goToAppStore}
                src={GooglePlayStore}
                alt="google play store"
              />
            </div>
          </TopInstallInstructions>
          <TopInstallInstructions heading="Option 3: Download it here">
            <p className="flex flex-1 text-base">
              Click the button below to automatically send it to your mobile phone. You must be
              logged into your Google account for this to work.
            </p>
            <button
              className="rounded-8 text-sm font-medium bg-grey-100 py-[10px]"
              onClick={onClickGetItHere}
            >
              Get it here
            </button>
          </TopInstallInstructions>
        </div>
      </section>

      <section className="flex flex-col items-center w-full gap-4 md:hidden">
        <HeadingOne>We're redirecting you</HeadingOne>
        <p className="text-center text-lg font-normal w-[min(380px,_100%)]">
          To continue you need to download the Tap On Phone app from the Google PlayStore.
        </p>

        <div className="w-6 h-1 m-0 mb-4 bg-red-600" />

        <PrimaryButton onClick={goToAppStore}>Take me to the Google PlayStore</PrimaryButton>
      </section>
    </Modal>
  );
};
